/**
 * Shared user profile types for API contracts between frontend and backend
 * Combines User, UserPersonalInfo, and Employment data
 */
import { z } from "zod";
import { uuidSchema, phoneSchema, emailSchema, timeHhmmSchema } from "./validation-common.js";
import { EeoEthnicitySchema, EeoGenderSchema, EeoClassificationSchema } from "./eeoCategories.js";
import { IdentifiedGenderSchema, LegalGenderSchema, PronounsSchema } from "./genderPronouns.js";
import { TtocCodeSchema } from "./ttocCodes.js";
// ===== QUERY SCHEMAS =====
/**
 * Query schema for GET /api/user-profile
 * Requires companyId for ACL-based authorization
 */
export const GetUserProfileQuerySchema = z.object({
    companyId: uuidSchema,
});
/**
 * Path parameter schema for GET /api/user-profile/:userId
 * Manager view of another user's profile
 */
export const GetUserProfileParamSchema = z.object({
    userId: uuidSchema,
});
// ===== UPDATE SCHEMAS =====
/**
 * Schema for updating user-level fields
 * Used in the user profile update endpoint
 */
export const UpdateUserFieldsSchema = z.object({
    email: emailSchema.optional(),
    firstName: z.string().trim().min(1).max(100).optional(),
    lastName: z.string().trim().min(1).max(100).optional(),
    preferredName: z.string().trim().max(100).nullable().optional(),
    phone: phoneSchema.nullable().optional(),
});
/**
 * Schema for updating personal info fields (UserPersonalInfo model)
 */
export const UpdatePersonalInfoFieldsSchema = z.object({
    emergencyContactName: z.string().trim().max(200).nullable().optional(),
    emergencyContactPhone: phoneSchema.nullable().optional(),
    emergencyContactRelation: z.string().trim().max(100).nullable().optional(),
    identifiedGender: IdentifiedGenderSchema.nullable().optional(),
    legalGender: LegalGenderSchema.nullable().optional(),
    pronouns: PronounsSchema.nullable().optional(),
    pronounsCustom: z.string().trim().max(100).nullable().optional(),
    eeoEthnicity: EeoEthnicitySchema.nullable().optional(),
    eeoGender: EeoGenderSchema.nullable().optional(),
    eeoClassification: EeoClassificationSchema.nullable().optional(),
    ssnLastFour: z.string().regex(/^\d{4}$/, "Must be exactly 4 digits").nullable().optional(),
});
/**
 * Schema for updating employment-level fields
 */
export const UpdateEmploymentFieldsSchema = z.object({
    companyEmail: emailSchema.nullable().optional(),
    irsJobCode: z.string().trim().max(50).nullable().optional(),
    ttocCode: TtocCodeSchema.nullable().optional(),
});
/**
 * Combined update schema for PUT /api/user-profile
 * All fields are optional - partial updates supported
 */
export const UpdateUserProfileInputSchema = z.object({
    companyId: uuidSchema,
    user: UpdateUserFieldsSchema.optional(),
    personalInfo: UpdatePersonalInfoFieldsSchema.optional(),
    employment: UpdateEmploymentFieldsSchema.optional(),
}).refine((data) => data.user || data.personalInfo || data.employment, { message: "At least one of user, personalInfo, or employment must be provided" });
// ===== TEAM MEMBER INFO TYPES =====
/**
 * Employment type enum values (matches Prisma EmploymentType)
 */
export const EmploymentTypeValues = [
    "FULL_TIME",
    "PART_TIME",
    "CONTRACT",
    "TEMPORARY",
    "INTERN",
];
// ===== QUICK STATS TYPES =====
/**
 * Query schema for GET /api/user-profile/quick-stats
 * Dashboard quick stats for the current user
 */
export const GetQuickStatsQuerySchema = z.object({
    companyId: uuidSchema,
});
// ===== RESPONSE SCHEMAS FOR ORPC =====
export const JobRoleFamilyBriefSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string().nullable(),
});
export const UserProfileUserResponseSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    preferredName: z.string().nullable(),
    phone: z.string().nullable(),
    photoUrl: z.string().nullable(),
});
export const UserProfilePersonalInfoResponseSchema = z.object({
    emergencyContactName: z.string().nullable(),
    emergencyContactPhone: z.string().nullable(),
    emergencyContactRelation: z.string().nullable(),
    identifiedGender: z.string().nullable(),
    legalGender: z.string().nullable(),
    pronouns: z.string().nullable(),
    pronounsCustom: z.string().nullable(),
    eeoEthnicity: z.string().nullable(),
    eeoGender: z.string().nullable(),
    eeoClassification: z.string().nullable(),
    ssnLastFour: z.string().nullable(),
});
export const UserProfileEmploymentResponseSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    companyEmail: z.string().nullable(),
    irsJobCode: z.string().nullable(),
    ttocCode: z.string().nullable(),
    isActive: z.boolean(),
    employmentEndDate: z.string().nullable(),
});
export const EligibleLocationSchema = z.object({
    id: z.uuid(),
    name: z.string().nullable(),
    addressLine1: z.string(),
    addressLine2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
});
export const UserProfileResponseSchema = z.object({
    user: UserProfileUserResponseSchema,
    personalInfo: UserProfilePersonalInfoResponseSchema,
    employment: UserProfileEmploymentResponseSchema,
    jobRoleFamilies: z.array(JobRoleFamilyBriefSchema),
    eligibleLocations: z.array(EligibleLocationSchema),
});
export const QuickStatsResponseSchema = z.object({
    ptoBalance: z.number(),
    sickLeaveBalance: z.number(),
    upcomingShifts: z.number(),
    pendingRequests: z.number(),
});
export const TeamMemberLocationSchema = z.object({
    id: z.uuid(),
    name: z.string().nullable(),
    addressLine1: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
});
export const TeamMemberManagerSchema = z.object({
    id: z.uuid(),
    firstName: z.string(),
    lastName: z.string(),
    photoUrl: z.string().nullable(),
});
export const TeamMemberJobRoleSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string().nullable(),
    jobRoleFamilyId: z.uuid(),
    jobRoleFamilyName: z.string(),
});
export const SkillExpirationStatusSchema = z.enum(["normal", "expiring", "expired"]);
export const TeamMemberSkillSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    expirationDate: z.string().nullable(), // Already ISO string from service
    expirationStatus: SkillExpirationStatusSchema,
});
export const TeamMemberInfoResponseSchema = z.object({
    compensationType: z.string().nullable(),
    employmentType: z.string().nullable(),
    laborLawClassification: z.string().nullable(),
    locations: z.array(TeamMemberLocationSchema),
    managers: z.array(TeamMemberManagerSchema),
    jobRoles: z.array(TeamMemberJobRoleSchema),
    skills: z.array(TeamMemberSkillSchema),
    employmentStartDate: z.string().nullable(), // Already ISO string from service
    birthday: z.string().nullable(), // Already ISO string from service
    isActive: z.boolean(),
    employmentEndDate: z.string().nullable(),
    userId: z.uuid(),
    employeeId: z.string().nullable(),
    payrollId: z.string().nullable(),
    permanentProfileId: z.string().nullable(),
    canViewCompensation: z.boolean().optional(),
});
export const JobCompensationSchema = z.object({
    id: z.uuid(),
    jobRoleFamilyId: z.uuid(),
    jobRoleFamilyName: z.string(),
    jobRoleFamilyColor: z.string().nullable(),
    compensationType: z.string(),
    payRate: z.number(),
    effectiveDate: z.string(), // Already ISO string from service
    status: z.enum(["ACTIVE", "PENDING"]),
    tipEligible: z.boolean(),
    commissionEligible: z.boolean(),
    bonusEligible: z.boolean(),
    notes: z.string().nullable(),
});
export const CompensationResponseSchema = z.object({
    compensations: z.array(JobCompensationSchema),
});
export const TimeRangeSchema = z.object({
    startTime: timeHhmmSchema,
    endTime: timeHhmmSchema,
});
export const AvailabilityDayOfWeekSchema = z.enum([
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
]);
export const DayAvailabilitySchema = z.object({
    dayOfWeek: AvailabilityDayOfWeekSchema,
    preferred: z.array(TimeRangeSchema),
    available: z.array(TimeRangeSchema),
});
export const DesiredHoursSchema = z.object({
    minShiftsPerWeek: z.number().nullable(),
    maxShiftsPerWeek: z.number().nullable(),
    minShiftLengthMinutes: z.number().nullable(),
    maxShiftLengthMinutes: z.number().nullable(),
    minWeeklyHours: z.number().nullable(),
    maxWeeklyHours: z.number().nullable(),
});
/**
 * Schedule block input schema for update endpoint
 * Uses ISO datetime strings for time representation
 */
export const ScheduleBlockInputSchema = z.object({
    dayOfWeek: AvailabilityDayOfWeekSchema,
    startTime: timeHhmmSchema,
    endTime: timeHhmmSchema,
});
/**
 * Operating hours block schema for location operating hours
 * Uses HH:mm format for time representation
 */
export const OperatingHoursBlockSchema = z.object({
    startTime: z.string(), // HH:mm format
    endTime: z.string(), // HH:mm format
});
/**
 * Update availability input schema for PUT endpoint
 * Replace-all strategy: arrays completely replace existing data
 */
export const UpdateAvailabilityInputSchema = z.object({
    preferredHours: z.array(ScheduleBlockInputSchema).optional(),
    availableHours: z.array(ScheduleBlockInputSchema).optional(),
});
export const AvailabilityResponseSchema = z.object({
    weeklyAvailability: z.array(DayAvailabilitySchema),
    desiredHours: DesiredHoursSchema,
    notes: z.string().nullable(),
    operatingHours: z
        .record(AvailabilityDayOfWeekSchema, z.array(OperatingHoursBlockSchema))
        .nullable(),
});
